var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "@ui/utils/toaster";
import { createNewUser, updateUser, } from "../../../../reducers/users/thunk/user-thunk";
import { useAppSelector } from "@application/store/useStore";
import { userEditStorage } from "@utility/user-edit-storage";
export const useUserForm = ({ isEdit, selectedUser }) => {
    const navigate = useNavigate();
    const { t } = useTranslation(["common"]);
    const methods = useForm({
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            roleIds: [],
            selectedRole: "",
            isActive: true,
            isEmailVerified: false,
            profilePictureUrl: "",
            userGroupIds: [],
            accessPoints: [],
        },
    });
    const { handleSubmit, control, reset, formState: { errors }, } = methods;
    const validationRules = {
        firstName: {
            required: "First name is required",
            pattern: {
                value: /^[A-Za-z]+$/,
                message: "First name should contain only letters",
            },
        },
        lastName: {
            required: "Last name is required",
            pattern: {
                value: /^[A-Za-z][A-Za-z\s'-]*$/,
                message: "Last name should contain only letters, spaces, hyphens, or apostrophes",
            },
        },
        email: {
            required: "Email is required",
            pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\s*$/i,
                message: "Invalid email format",
            },
        },
        selectedRole: {
            required: "Role is required",
        },
    };
    useEffect(() => {
        if (isEdit && selectedUser) {
            reset({
                id: selectedUser.id,
                firstName: selectedUser.firstName,
                lastName: selectedUser.lastName,
                email: selectedUser.email,
                isActive: selectedUser.isActive,
                isEmailVerified: false,
                profilePictureUrl: "",
                roleIds: selectedUser.roleIds,
                selectedRole: selectedUser.roleIds[0] || "",
                accessPoints: selectedUser.accessPoints || [],
            });
        }
        else if (isEdit && !selectedUser) {
            const storedUserData = userEditStorage.getUserEditData();
            if (storedUserData) {
                reset({
                    id: storedUserData.id,
                    firstName: storedUserData.firstName,
                    lastName: storedUserData.lastName,
                    email: storedUserData.email,
                    isActive: storedUserData.isActive,
                    isEmailVerified: false,
                    profilePictureUrl: "",
                    roleIds: storedUserData.roleIds,
                    selectedRole: storedUserData.selectedRole || storedUserData.roleIds[0] || "",
                    userGroupIds: storedUserData.userGroupIds || [],
                    accessPoints: storedUserData.accessPoints || [],
                });
            }
        }
        // Cleanup: Clear local storage when navigating away from add user page
        return () => {
            if (!isEdit) {
                userEditStorage.clearUserEditData();
            }
        };
    }, [isEdit, selectedUser, reset]);
    const dispatch = useDispatch();
    const { createLoading, updateLoading } = useAppSelector((state) => state.user);
    const handleFormSubmit = (formData) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            // Get user ID from selectedUser or from form data (loaded from local storage)
            const userId = (selectedUser === null || selectedUser === void 0 ? void 0 : selectedUser.id) || formData.id;
            const commandData = {
                id: isEdit ? userId : undefined,
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                roleIds: formData.selectedRole && formData.selectedRole.trim() !== ""
                    ? [formData.selectedRole]
                    : [],
                // Backend expects List<string>
                accessPoints: (formData.accessPoints || []).map((ap) => typeof ap === "string" ? ap : (ap === null || ap === void 0 ? void 0 : ap.id) || (ap === null || ap === void 0 ? void 0 : ap.value) || String(ap)),
                isActive: formData.isActive,
            };
            if (isEdit) {
                dispatch(updateUser(commandData))
                    .unwrap()
                    .then(() => {
                    toast.success(t("common:toasts.userUpdatedSuccessfully"));
                    // Clear local storage after successful update
                    userEditStorage.clearUserEditData();
                    navigate("/users");
                })
                    .catch((error) => {
                    console.error("Error updating user:", error);
                    if (error.errors) {
                        error.errors.forEach((errorMsg) => {
                            toast.error(errorMsg);
                        });
                    }
                    else {
                        toast.error(error.message || t("common:toasts.failedToUpdateUser"));
                    }
                });
            }
            else {
                dispatch(createNewUser(commandData))
                    .unwrap()
                    .then((msg) => {
                    toast.success(msg.message);
                    navigate("/users");
                })
                    .catch((error) => {
                    var _a;
                    console.error("Error creating user:", error);
                    const msg = typeof error === "string" ? error : error === null || error === void 0 ? void 0 : error.message;
                    if (msg === null || msg === void 0 ? void 0 : msg.toLowerCase().includes("email already exists")) {
                        toast.error(t("common:toasts.emailAlreadyExists"));
                    }
                    else if ((_a = error === null || error === void 0 ? void 0 : error.errors) === null || _a === void 0 ? void 0 : _a.length) {
                        error.errors.forEach((errorMsg) => {
                            toast.error(errorMsg);
                        });
                    }
                    else {
                        toast.error(msg || t("common:toasts.failedToCreateUser"));
                    }
                });
            }
        }
        catch (error) {
            console.error("Error submitting form:", error);
        }
    });
    return {
        methods,
        control,
        errors,
        handleSubmit,
        validationRules,
        handleFormSubmit,
        isLoading: isEdit ? updateLoading : createLoading,
    };
};
